import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { VoiceConfig } from '@/utils/voiceUtils';
import { voicePresets } from '@/utils/voicePresets';
import { Button } from '@/components/ui/button';
import MainNav from '@/components/MainNav';
import { Accessibility, Mic, UserCheck, Code, ArrowRight, Contrast, Type, Underline } from 'lucide-react';
import Footer from '@/components/Footer';

const demoSteps = [
  {
    number: 1,
    title: 'Open Accessibility Menu',
    description:
      "Click the Accessibility button at the bottom left to open options for contrast, font size, dark mode, and more.",
    icon: <Accessibility className="h-6 w-6 text-primary" />,
    highlight: "accessibility"
  },
  {
    number: 2,
    title: 'Use the Voice Assistant',
    description:
      "Click the microphone button at the bottom center to give voice commands like navigation and accessibility toggles.",
    icon: <Mic className="h-6 w-6 text-primary" />,
    highlight: "voice"
  },
  {
    number: 3,
    title: 'See Changes Instantly',
    description:
      "Try both menus. Adjust settings visually and with your voice. Observe instant feedback and accessibility improvements.",
    icon: (
      <svg className="h-6 w-6 text-primary" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={2} />
        <path d="M8 12.5l2 2 4-4" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    highlight: null
  }
];

const sampleCommands = [
  { command: "Go to pricing", explanation: "Navigate to pricing page" },
  { command: "Enable large pointer", explanation: "Larger cursor for easier viewing" },
  { command: "Search for subscription", explanation: "In-app keyword search" },
  { command: "Increase font size", explanation: "Improve text readability" },
  { command: "Help", explanation: "Hear all supported commands" },
];

const features = [
  "Accessibility menu: visual settings (contrast, dark mode, text, pointer)",
  "Voice navigation between pages",
  "Toggle dark mode & font sizes by voice or menu",
  "Visual accessibility toggles",
  "Live feedback (voice & text)",
  "Supports compound voice commands",
  "Custom plugin support"
];

const presetPanelClass = "p-4 border rounded-lg bg-muted/60 mb-8";

const DemoPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAssistantActive, setIsAssistantActive] = useState(true);
  const [selectedPresetId, setSelectedPresetId] = useState(searchParams.get('preset') || 'basic');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const selectedPreset = voicePresets.find(preset => preset.id === selectedPresetId);
  const getConfigForPreset = (presetId: string): VoiceConfig => {
    const preset = voicePresets.find(p => p.id === presetId);
    if (preset) {
      try {
        const customCommands = JSON.parse(preset.config.customCommands || '{}');
        const customResponses = JSON.parse(preset.config.customResponses || '{}');
        return {
          commands: customCommands,
          customResponses: customResponses,
        };
      } catch (e) {
        return {};
      }
    }
    return {};
  };

  const [config, setConfig] = useState<VoiceConfig>(() => getConfigForPreset(selectedPresetId));

  const handlePresetChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const presetId = event.target.value;
    setSelectedPresetId(presetId);
    setSearchParams({ preset: presetId });
    setConfig(getConfigForPreset(presetId));
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-[#fff] via-[#F2FCE2] to-[#E5DEFF] flex flex-col">
      <header>
        <MainNav currentPath="/demo" />
      </header>

      <div className="container mx-auto max-w-3xl pt-10 pb-32 px-4 flex-1">
        <h1 className="text-4xl font-extrabold mb-4 text-center bg-gradient-to-r from-purple-700 via-primary-500 to-blue-400 text-transparent bg-clip-text">
          Accesstopia Widget Demo
        </h1>
        <p className="text-lg text-center text-gray-700 mb-7">
          Experience <span className="font-semibold text-primary">both</span> Accessibility Options menu and Voice Assistant capabilities live below.<br />
          Use <strong>both the bottom left</strong> and <strong>bottom center</strong> buttons to access unique accessibility features!
        </p>

        <div className="grid md:grid-cols-3 gap-3 mb-8">
          {demoSteps.map((step, idx) => (
            <div key={step.number} className={`flex flex-col items-center p-4 bg-white rounded-xl shadow border border-border hover-scale transition text-center`}>
              <div className={`flex items-center justify-center w-12 h-12 rounded-full mb-3 ${step.highlight === "accessibility" && 'bg-yellow-300/10'} ${step.highlight === "voice" && 'bg-purple-300/20'} ${!step.highlight && 'bg-primary/10'}`}>
                {step.icon}
              </div>
              <h2 className="font-semibold text-primary text-base">{`Step ${step.number}: ${step.title}`}</h2>
              <p className="text-sm text-gray-700">{step.description}</p>
            </div>
          ))}
        </div>

        <div className="mb-6 grid sm:grid-cols-2 gap-4">
          {features.map((feature, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2 shadow-sm text-gray-700">
              <UserCheck className="text-primary h-5 w-5" />
              <span className="text-sm">{feature}</span>
            </div>
          ))}
        </div>

        <div className="mb-9">
          <div className="mb-2 text-base font-semibold text-gray-800">Popular voice commands:</div>
          <div className="flex flex-wrap gap-3">
            {sampleCommands.map(sample => (
              <div key={sample.command} className="flex flex-col rounded-md bg-muted/80 px-4 py-2 text-primary shadow-sm border hover-scale transition min-w-[170px]">
                <span className="font-mono text-base font-medium">&quot;{sample.command}&quot;</span>
                <span className="text-xs text-gray-600">{sample.explanation}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={presetPanelClass}>
          <button
            onClick={() => setShowAdvanced(show => !show)}
            className="font-bold text-sm text-primary hover:underline mb-2 focus:outline-none"
            aria-expanded={showAdvanced}
            aria-controls="advanced-panel"
          >
            {showAdvanced ? 'Hide' : 'Show'} Advanced / Preset Options
          </button>
          {showAdvanced && (
            <div id="advanced-panel" className="animate-fade-in">
              <div className="mb-3">
                <label htmlFor="presetSelect" className="block text-xs font-semibold text-gray-700 mb-1">
                  Select Voice Preset:
                </label>
                <select
                  id="presetSelect"
                  className="w-full border border-border rounded-lg py-2 px-3 focus:outline-primary"
                  value={selectedPresetId}
                  onChange={handlePresetChange}
                >
                  {voicePresets.map(preset => (
                    <option key={preset.id} value={preset.id}>{preset.name}</option>
                  ))}
                </select>
                {selectedPreset && (
                  <p className="mt-1 text-xs text-gray-500">
                    <strong>Description:</strong> {selectedPreset.description}
                  </p>
                )}
              </div>
              <div>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/command-debugger" className="inline-flex items-center gap-1">
                    <Code className="h-4 w-4" /> Voice Command Debugger
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="w-full bg-gradient-to-r from-primary/90 to-purple-600 text-white py-10 mt-auto relative z-20 flex flex-col items-center">
        <div className="container max-w-2xl px-5 mx-auto flex flex-col items-center text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">Ready to make your site accessible to all?</h2>
          <p className="text-base md:text-lg mb-5 max-w-xl">
            The <span className="font-semibold">Accesstopia Accessibility Widget</span> lets anyone adjust:
            <span className="underline ml-1">contrast, font size, dark mode, pointer size, link/UI highlights, text style, voice commands, and more</span> 
            – all instantly and without code.
          </p>
          <ul className="text-left text-white/90 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2">
            <li>
              <Contrast className="inline mr-2 text-white" size={18} />
              Contrast / dark mode toggles
            </li>
            <li>
              <Mic className="inline mr-2 text-white" size={18} />
              Voice commands for navigation
            </li>
            <li>
              <Type className="inline-block w-5 h-5 align-middle mr-2 text-white" />
              Resize fonts, center/underline/cursor
            </li>
            <li>
              <Underline className="inline-block w-5 h-5 align-middle mr-2 text-white" />
              Highlight links & text for clarity
            </li>
          </ul>
          <Button asChild size="lg" variant="secondary" className="font-bold text-lg px-7 py-3 flex items-center gap-2 bg-black text-white hover:bg-black/90 hover:text-white">
            <Link to="/pricing" aria-label="See Accessibility Pricing Options">
              See plans & pricing <ArrowRight className="inline-block ml-1" />
            </Link>
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DemoPage;
